import { slugify } from '@/utils/string';
import clsx from 'clsx';
import { AlertCircle, Check } from 'lucide-react';
import React from 'react';

export interface AppCheckboxProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'type' | 'className'
  > {
  label?: React.ReactNode;
  error?: string;
  className?: string;
  inputClassName?: string;
}

const AppCheckbox = React.forwardRef<HTMLInputElement, AppCheckboxProps>(
  (
    { label, error, className = '', inputClassName = '', id, ...inputProps },
    ref
  ) => {
    const checkboxId =
      id ??
      (typeof label === 'string' ? `checkbox-${slugify(label)}` : undefined);

    const isDisabled = inputProps.disabled;

    return (
      <div className={clsx('space-y-1', className)}>
        <label
          htmlFor={checkboxId}
          className={clsx(
            'flex items-center gap-2.5 cursor-pointer select-none',
            isDisabled && 'cursor-not-allowed opacity-50'
          )}
        >
          <div className="relative mt-px shrink-0">
            <input
              ref={ref}
              type="checkbox"
              id={checkboxId}
              className={clsx(
                'peer absolute inset-0 w-5 h-5 cursor-pointer opacity-0 disabled:cursor-not-allowed',
                inputClassName
              )}
              aria-invalid={Boolean(error)}
              aria-describedby={
                error && checkboxId ? `${checkboxId}-error` : undefined
              }
              disabled={isDisabled}
              {...inputProps}
            />
            <span
              className={clsx(
                'inline-flex items-center justify-center w-5 h-5 rounded-[5px] border-[1.5px] bg-white text-white transition-colors',
                'peer-checked:bg-primary peer-checked:border-primary peer-checked:[&>svg]:opacity-100',
                'peer-focus-visible:border-primary',
                error ? 'border-danger' : 'border-grey'
              )}
            >
              <Check className="w-3.5 h-3.5 stroke-[3] opacity-0" aria-hidden />
            </span>
          </div>
          {label != null && (
            <span className="flex-1 text-[13.5px] leading-snug text-textLight">
              {label}
            </span>
          )}
        </label>
        {error && (
          <p
            id={checkboxId ? `${checkboxId}-error` : undefined}
            className="mt-1 flex items-center gap-1 text-[13px] text-danger"
          >
            <AlertCircle className="size-4 shrink-0" aria-hidden />
            {error}
          </p>
        )}
      </div>
    );
  }
);

AppCheckbox.displayName = 'AppCheckbox';

export default AppCheckbox;
